import React, { useState, useEffect, useCallback } from "react";

import {
  GitBranch,
  Filter,
  RefreshCw,
  Target,
  CheckCircle2,
  XCircle,
  Activity,
} from "lucide-react";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";

import { motion } from "framer-motion";

import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import MISFilterDrawer from "../MISFilterDrawer";


const PipelineDistributionSkeleton = () => {
  return (
    <div className="bg-white rounded-2xl border p-5 animate-pulse">
      <div className="h-6 w-52 bg-gray-200 rounded mb-6" />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-24 rounded-xl bg-gray-100" />
        ))}
      </div>

      <div className="h-[420px] rounded-xl bg-gray-100" />
    </div>
  );
};

const PipelineDistributionError = ({ onRetry }) => {
  return (
    <div className="bg-white rounded-2xl border border-red-200 p-6 text-center">
      <h3 className="text-red-600 font-semibold">
        Failed to load Pipeline Distribution
      </h3>

      <button
        onClick={onRetry}
        className="mt-4 px-4 py-2 rounded-lg bg-red-600 text-white"
      >
        Retry
      </button>
    </div>
  );
};

const COLORS = [
  "#DC2626",
  "#EF4444",
  "#F97316",
  "#F59E0B",
  "#84CC16",
  "#22C55E",
  "#2563EB",
  "#7C3AED",
];

const PipelineDistributionCard = () => {
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(false);

  const [data, setData] = useState(null);

  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    filterType: "thisMonth",
    divisionId: "",
    userId: "",
    stageId: "",
    industryType: "",
    fromDate: "",
    toDate: "",
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);

      const params = new URLSearchParams();

      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });

      const response = await api.get(
        `${SummaryApi.getPipelineDistribution.url}?${params.toString()}`,
      );

      if (response?.data?.success) {
        setData(response.data.data);
      }
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (loading) return <PipelineDistributionSkeleton />;

  if (error) return <PipelineDistributionError onRetry={fetchReport} />;

  const summary = data.summary;

  const cards = [
    {
      title: "Total Leads",
      value: summary.totalLeads,
      icon: Target,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      title: "Active",
      value: summary.activeLeads,
      icon: Activity,
      color: "text-orange-600",
      bg: "bg-orange-50",
    },
    {
      title: "Won",
      value: summary.wonLeads,
      icon: CheckCircle2,
      color: "text-green-600",
      bg: "bg-green-50",
    },
    {
      title: "Lost",
      value: summary.lostLeads,
      icon: XCircle,
      color: "text-red-600",
      bg: "bg-red-50",
    },
    {
      title: "Not Interested",
      value: summary.notInterestedLeads,
      icon: GitBranch,
      color: "text-purple-600",
      bg: "bg-purple-50",
    },
  ];

  return (
    <>
      <motion.div
        initial={{
          opacity: 0,
          y: 20,
        }}
        whileInView={{
          opacity: 1,
          y: 0,
        }}
        viewport={{
          once: true,
        }}
        className="bg-white rounded-2xl border shadow-sm"
      >
        <div className="flex justify-between items-center p-5 border-b">
          <div className="flex items-center gap-2">
            <GitBranch className="text-red-600" size={18} />

            <h2 className="font-semibold text-lg">Pipeline Distribution</h2>
          </div>

          <div className="flex gap-2">
            <button
              onClick={fetchReport}
              className="h-9 w-9 rounded-lg border flex items-center justify-center"
            >
              <RefreshCw size={15} />
            </button>

            <button
              onClick={() => setShowFilters(true)}
              className="h-9 w-9 rounded-lg bg-red-600 text-white flex items-center justify-center"
            >
              <Filter size={15} />
            </button>
          </div>
        </div>

        <div className="p-5">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            {cards.map((item) => {
              const Icon = item.icon;

              return (
                <div key={item.title} className={`${item.bg} rounded-xl p-4`}>
                  <div className="flex justify-between">
                    <div>
                      <p className="text-xs text-gray-500">{item.title}</p>

                      <h3 className={`mt-2 text-xl font-bold ${item.color}`}>
                        {item.value}
                      </h3>
                    </div>

                    <Icon size={20} className={item.color} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid xl:grid-cols-2 gap-6">
            <div className="border rounded-xl p-4 h-[420px]">
              <h3 className="font-semibold mb-4">Stage Distribution</h3>

              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.chart} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis type="number" />

                  <YAxis dataKey="stageName" type="category" width={120} />

                  <Tooltip />

                  <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                    {data.chart.map((entry, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="border rounded-xl p-5">
              <h3 className="font-semibold mb-4">Funnel Overview</h3>

              <div className="space-y-3">
                {data.pipeline.map((item) => (
                  <div key={item.stageId}>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{item.stageName}</span>

                      <span className="font-semibold">{item.count}</span>
                    </div>

                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-red-600 rounded-full"
                        style={{
                          width: `${item.percentage}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-xl border overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">Stage</th>

                  <th className="px-4 py-3 text-left">Type</th>

                  <th className="px-4 py-3 text-left">Leads</th>

                  <th className="px-4 py-3 text-left">%</th>
                </tr>
              </thead>

              <tbody>
                {data.pipeline.map((row) => (
                  <tr key={row.stageId} className="border-t">
                    <td className="px-4 py-3">{row.stageName}</td>

                    <td className="px-4 py-3 capitalize">{row.stageType}</td>

                    <td className="px-4 py-3">{row.count}</td>

                    <td className="px-4 py-3">{row.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-5">
            <h3 className="font-semibold text-red-700 mb-3">
              Management Insights
            </h3>

            <ul className="space-y-2">
              {data.insights.map((item, index) => (
                <li key={index} className="text-sm text-gray-700">
                  • {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.div>

      <MISFilterDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        setFilters={setFilters}
        onApply={() => {
          setAppliedFilters(filters);
          setShowFilters(false);
        }}
      />
    </>
  );
};

export default PipelineDistributionCard;
